// Parent dashboard: live cards (children's absences + outstanding balance) via
// DynamicDashboard, plus today's courses for each linked child.
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronRight, Clock, Loader2, MapPin, User, UserX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DynamicDashboard } from '@/shared/components/dashboard';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { fetchMyChildren, fetchChildSchedule, fetchChildrenAbsences, ParentChild } from '../services/parentService';

interface ChildScheduleEntry {
  id: string;
  startTime: string;
  endTime: string;
  moduleElement?: { name?: string };
  professor?: { firstName?: string; lastName?: string };
  room?: { name?: string };
  sessions?: { date: string; startTime: string; endTime: string }[];
}

const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ChildTodayCourses: React.FC<{ child: ParentChild }> = ({ child }) => {
  const today = todayStr();
  const { data, isLoading } = useQuery({
    queryKey: ['parent-child-today-courses', child.studentId, today],
    queryFn: () => fetchChildSchedule(child.studentId, today, today) as Promise<ChildScheduleEntry[]>,
  });

  const courses = (Array.isArray(data) ? data : []).slice().sort((a, b) => {
    const ta = a.sessions?.[0]?.startTime || a.startTime;
    const tb = b.sessions?.[0]?.startTime || b.startTime;
    return ta.localeCompare(tb);
  });

  return (
    <div className="border-t border-gray-100 first:border-t-0">
      <p className="px-6 pt-4 pb-1 text-sm font-semibold text-gray-700">
        {child.firstName} {child.lastName}
      </p>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
        </div>
      ) : courses.length === 0 ? (
        <p className="px-6 pb-4 text-sm text-gray-500">Aucun cours aujourd'hui</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {courses.map((entry) => {
            const session = entry.sessions?.[0];
            const professorName = entry.professor
              ? `${entry.professor.firstName || ''} ${entry.professor.lastName || ''}`.trim()
              : null;
            return (
              <div key={entry.id} className="px-6 py-3">
                <div className="flex items-center gap-2 mb-0.5">
                  <Clock className="h-3.5 w-3.5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-600">
                    {session?.startTime || entry.startTime} - {session?.endTime || entry.endTime}
                  </span>
                </div>
                <p className="font-medium text-gray-900 text-sm">{entry.moduleElement?.name || 'Matière'}</p>
                <div className="flex flex-wrap gap-3 mt-0.5 text-xs text-gray-500">
                  {professorName && (
                    <span className="flex items-center gap-1"><User className="h-3 w-3" />{professorName}</span>
                  )}
                  {entry.room?.name && (
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{entry.room.name}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const ParentDashboard: React.FC = () => {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const { data: children } = useQuery({
    queryKey: ['parent-children', user?.id],
    queryFn: fetchMyChildren,
  });
  const { data: absences } = useQuery({
    queryKey: ['parent-children-absences', user?.id],
    queryFn: () => fetchChildrenAbsences(10),
  });

  return (
    <div>
      <DynamicDashboard
        title={`Bienvenue, ${user?.firstName}`}
        showRecentActivity={false}
      />

      {/* Today's courses for each linked child */}
      <div className="px-4 sm:px-6 pb-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h2 className="font-semibold text-gray-900">Cours d'aujourd'hui</h2>
            </div>
            <Link
              to={`/institution/${tenantId}/parent/emploi-du-temps`}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Voir l'emploi du temps
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {!children || children.length === 0 ? (
            <p className="px-6 py-4 text-sm text-gray-500">Aucun enfant lié à votre compte</p>
          ) : (
            children.map((child) => <ChildTodayCourses key={child.studentId} child={child} />)
          )}
        </div>

        {/* Recent absences across all children */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserX className="h-5 w-5 text-red-600" />
              <h2 className="font-semibold text-gray-900">Absences récentes</h2>
            </div>
            <Link
              to={`/institution/${tenantId}/parent/presences`}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Voir les présences
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
          {!absences || absences.length === 0 ? (
            <p className="px-6 py-4 text-sm text-gray-500">Aucune absence enregistrée</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {absences.map((a) => (
                <div key={a.id} className="px-6 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {a.studentName} — {a.courseName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(a.sessionDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      {a.justification ? ` — ${a.justification}` : ''}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                      a.status === 'ABSENT_EXCUSED' ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'
                    }`}
                  >
                    {a.status === 'ABSENT_EXCUSED' ? 'Justifiée' : 'Non justifiée'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
